import { GlobalStateController } from "bai-react-global-state";
import React from "react";

import { presentations as presentationsApi } from "apis/callables";
import { LIBRARY_RECENT_MAX } from "common/constants";
import { trackState } from "js/analytics";
import { getCanvasBundle } from "js/canvas";
import { appVersion } from "js/config";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { CollaborationSlidesLockService } from "js/core/services/collaborationSlidesLock";
import * as geom from "js/core/utilities/geom";
import { trackActivity } from "js/core/utilities/utilities";
import { app } from "js/namespaces";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowDialogAsync,
    ShowInputDialog,
    ShowWarningDialog,
    ShowErrorDialog
} from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { browserHistory } from "js/react/history";
import permissionsDS from "js/react/views/PresentationSettings/dataservices/PermissionsDataService";
import VersionConflictDialog from "js/react/components/Dialogs/VersionConflictDialog";
import { _ } from "js/vendor";

const logger = getLogger(LogGroup.EDITOR);

export const PanelType = {
    COLOR: "color",
    LAYOUT: "layout",
    CALLOUTS: "callouts",
    NOTES: "notes",
    VARIATIONS: "variations",
    COMMENTS: "comments",
    ASSIGN_SLIDE: "assign-slide",
    VERSION_HISTORY: "version-history",
    ELEMENT: "element",
    ELEMENT_PROPERTIES: "element-properties",
    ANIMATION: "animation",
    RECORD: "record",
    SHORTCUTS: "shortcuts",
    ADVANCE_ON: "advance-on",
    MORE_ACTIONS: "more-actions"
};

const PRE_RENDER_SLIDES_RANGE = 5;

const initialState = {
    isInitialized: false,
    currentSlide: null,
    presentation: null,
    activeRightPanel: null,
    activeBottomPanel: null,
    showCanvasControls: true,
    showSelectionLayer: true,
    isSingleSlideEditor: false,
    currentCanvasController: null,
    currentSelectionLayerController: null,
    showSlideGrid: false,
    canvasControllers: {},
    selectionLayerControllers: {},
    currentSlideLockOwner: null,
    isCurrentSlideLockedForMe: false,
    isCurrentSlideLockedByMe: false,
    allowThemeChange: true,
    popupOpenState: false,
    hidePanels: [],
    slides: [],
    currentTheme: null,
    freezePropertiesPanel: false,
    showAddElementDropZone: false,
    ElementPanel: null,
    elementPanelElement: null,
    selectedPropertyPanelTab: "element",
    selectedPropertyPanelTabProps: {},
    showPropertyPanel: true,
    handleKeyEvents: true
};

class PresentationEditorController extends GlobalStateController {
    constructor(initialState) {
        super(initialState);

        this.asyncActionsPromiseChain = Promise.resolve();
        this.collaborationSlidesLockService = null;
        this.collaborationSlidesLockServiceSubscription = null;
        this.unfreezePropertiesPanelTimeout = null;
        this.resetAppLoaderOnReset = true;
        this.pushStateTimeout = null;
    }

    get presentation() {
        return this._state.presentation;
    }

    setPresentation = presentation => {
        return this._updateState({ presentation });
    }

    get currentSlide() {
        return this._state.currentSlide;
    }

    get isInitialized() {
        return this._state.isInitialized;
    }

    get isSingleSlideEditor() {
        return this._state.isSingleSlideEditor;
    }

    get activeRightPanel() {
        return this._state.activeRightPanel;
    }

    get activeBottomPanel() {
        return this._state.activeBottomPanel;
    }

    get elementPanelElement() {
        return this._state.elementPanelElement;
    }

    get currentTheme() {
        return this._state.currentTheme;
    }

    get currentCanvasController() {
        return this._state.currentCanvasController;
    }

    _stateDidUpdate(prevState) {
        const { currentSlideLockOwner, currentSlide, slides, isInitialized } = this._state;

        if (isInitialized && prevState.currentSlideLockOwner !== currentSlideLockOwner) {
            if (currentSlideLockOwner) {
                this.hideSelectionLayer();
            } else {
                this.showSelectionLayer();
            }
        }

        if (currentSlide && prevState.currentSlide !== currentSlide) {
            this.renderCurrentAndSurroundingSlides();
        } else if (!_.isEqual(slides.map(s => s?.id), prevState.slides.map(s => s?.id)) && currentSlide) {
            this.renderCurrentAndSurroundingSlides();
        }
    }

    _stateWillUpdate(nextState) {
        const { currentSlideLockOwner } = nextState;
        nextState.isCurrentSlideLockedForMe = !!currentSlideLockOwner;
        return nextState;
    }

    async renderCurrentAndSurroundingSlides() {
        const { slides, canvasControllers, presentation } = this._state;

        if (!presentation) {
            return;
        }

        const currentSlideIndex = this.getCurrentSlideIndex();

        // Giving priority to the closest slides to the current one, i.e. current->next->prev->next+1->prev-1->next+2->prev-2->...
        const offsets = _.sortBy(_.range(-PRE_RENDER_SLIDES_RANGE, PRE_RENDER_SLIDES_RANGE).reverse(), i => Math.abs(i));
        const slideIndexes = offsets.map(offset => currentSlideIndex + offset).filter(slideIndex => slideIndex >= 0 && slideIndex < slides.length);

        for (const slideIndex of slideIndexes) {
            const slide = slides[slideIndex];
            const canvasController = canvasControllers[slide.id];
            if (canvasController) {
                await canvasController.renderCanvas()
                    .catch(err => logger.error(err, "[PresentationEditorController] renderCurrentAndSurroundingSlides() error rendering slide", { slideId: slide.id }));
            }

            if (!this._state.presentation) {
                // Editor was reset, stop rendering
                return;
            }

            if (currentSlideIndex !== this.getCurrentSlideIndex()) {
                // Current slide has changed, stop rendering (the new renderCurrentAndSurroundingSlides() call will take over)
                return;
            }
        }
    }

    /**
     * Wraps the component with PresentationEditorController state, current CanvasController and current SelectionLayerController states.
     * Use only when 100% have to, this is a performance killer if not used wisely.
     */
    withFullState(Component, shouldUpdate = (props, nextProps) => true) {
        class ComponentWithFullState extends React.Component {
            constructor(props) {
                super(props);

                this.wrappedComponents = {};
            }

            shouldComponentUpdate(nextProps) {
                return shouldUpdate(this.props, nextProps);
            }

            render() {
                const { currentCanvasController, currentSelectionLayerController, innerRef } = this.props;

                if (!currentCanvasController || !currentSelectionLayerController) {
                    return null;
                }

                const wrapperId = `${currentCanvasController.instanceId}-${currentSelectionLayerController.instanceId}`;

                // Creating new wrapper during render so we don't have to cause additional re-render
                if (!this.wrappedComponents[wrapperId]) {
                    this.wrappedComponents[wrapperId] =
                        currentCanvasController.withState(
                            currentSelectionLayerController.withState(
                                Component
                            )
                        );
                }

                const WrappedComponent = this.wrappedComponents[wrapperId];
                return <WrappedComponent {...this.props} ref={innerRef} />;
            }
        }

        return this.withState(React.forwardRef((props, ref) => <ComponentWithFullState {...props} innerRef={ref} />));
    }

    getCurrentCanvasController() {
        return this._state.currentCanvasController;
    }

    get canvasController() {
        return this.getCurrentCanvasController();
    }

    getCurrentSelectionLayerController() {
        return this._state.currentSelectionLayerController;
    }

    get selectionLayerController() {
        return this.getCurrentSelectionLayerController();
    }

    getCanvasControllerForSlide(slide) {
        return this._state.canvasControllers[slide.id];
    }

    getCanvasControllers() {
        return this._state.canvasControllers;
    }

    getPopupOpenState() {
        return this._state.popupOpenState;
    }

    getCanvases() {
        return Object.values(this._state.canvasControllers)
            .filter(c => c.canvas)
            .map(c => c.canvas);
    }

    getCurrentSlideIndex() {
        const { presentation, currentSlide } = this._state;
        return presentation.getSlideIndex(currentSlide.id);
    }

    canShowSelectionLayer(slide, canvasController, isSingleSlideEditor) {
        return (!slide?.isLibrarySlide() || isSingleSlideEditor) && !canvasController.isTemplateObsolete;
    }

    setShowAddElementDropZone(showAddElementDropZone) {
        return this._updateState({ showAddElementDropZone });
    }

    setHandleKeyEvents = handleKeyEvents => {
        return this._updateState({ handleKeyEvents });
    }

    async _reset() {
        logger.info("[PresentationEditorController] reset()");

        if (!this._state.isInitialized) {
            return;
        }

        const { presentation, currentCanvasController, currentSelectionLayerController, currentSlide, canvasControllers } = this._state;

        clearTimeout(this.pushStateTimeout);

        if (currentCanvasController) {
            await currentCanvasController.removeAsCurrentCanvas();
        }
        if (currentSelectionLayerController) {
            await currentSelectionLayerController.reset();
        }
        if (presentation) {
            presentation.off("change", this._onPresentationChange);
            presentation.off("slidesUpdated", this._onPresentationSlidesUpdated);
            presentation.off("remoteError", this._onPresentationRemoteError);
        }
        if (currentSlide) {
            currentSlide.off("change", this._onCurrentSlideChange);
        }

        Object.values(canvasControllers).forEach(canvasController => canvasController.dispose());

        ds.selection.presentation = null;

        app.undoManager.reset();

        if (this.collaborationSlidesLockServiceSubscription) {
            this.collaborationSlidesLockServiceSubscription.unsubscribe();
        }

        if (this.collaborationSlidesLockService) {
            await this.collaborationSlidesLockService.dispose();
            this.collaborationSlidesLockService = null;
        }

        await this._updateState(() => _.cloneDeep(initialState));

        if (this.resetAppLoaderOnReset) {
            window.appLoaderController.resetAppState();
        }
    }

    async reset(runSequentially = true) {
        // remove listeners for offline status changes
        window.removeEventListener("offline", this.onWindowOffline);
        window.removeEventListener("online", this.onWindowOnline);

        if (!runSequentially) {
            return this._reset();
        }

        return new Promise((resolve, reject) => {
            this.asyncActionsPromiseChain = this.asyncActionsPromiseChain
                .then(() => this._reset())
                .then(resolve)
                .catch(reject);
        });
    }

    async _instantiateCanvasController(presentation, slide) {
        const { CanvasController } = await import(/* webpackMode: "eager" */ "js/editor/PresentationEditor/CanvasController");

        return new CanvasController({
            presentation,
            slide,
            canvasWidth: 1280,
            canvasHeight: 720,
            onCanvasRendered: this.onCanvasRendered
        }, this.collaborationSlidesLockService);
    }

    async _instantiateSelectionLayerController(canvasController) {
        const { SelectionLayerController } = await import(/* webpackMode: "eager" */ "js/editor/PresentationEditor/SelectionLayerController");

        return new SelectionLayerController({ canvasController });
    }

    onWindowOnline = () => this._onOfflineStatusChange(true);
    onWindowOffline = () => this._onOfflineStatusChange(false);

    _onOfflineStatusChange = isOnline => {
        if (isOnline) {
            const presentation = this._state.presentation;

            // reload the presentation to get the latest data when coming back online
            logger.info("[PresentationEditorController] onOfflineStatusChange()", { presentationId: presentation.id });
            presentation.reload()
                .catch(err => logger.error(err, "[PresentationEditorController] error reloading presentation"));
        }
    }

    async loadPresentation(presentation, slideIndex = 0, isSingleSlideEditor = false, allowThemeChange = true, hidePanels = []) {
        return new Promise((resolve, reject) => {
            this.asyncActionsPromiseChain = this.asyncActionsPromiseChain
                .then(async () => {
                    logger.info("[PresentationEditorController] loadPresentation()", { presentationId: presentation.id, slideIndex, isSingleSlideEditor, allowThemeChange, hidePanels });

                    // Force reset state
                    await this.reset(false);

                    // listen for offline status changes
                    window.addEventListener("offline", this.onWindowOffline);
                    window.addEventListener("online", this.onWindowOnline);

                    await this._updateState({
                        isSingleSlideEditor,
                        allowThemeChange,
                        hidePanels
                    });

                    await presentation.load();

                    const presentationOwnerId = presentation.get("userId");
                    const presentationIsTemplate = presentation.get("isTemplate") === true;
                    const presentationIsDummy = presentation.get("isDummy") === true;
                    const presentationIsMigrated = presentation.get("_migrated") === true;

                    if (!presentationIsDummy && !this.isSingleSlideEditor) {
                        if (!presentationIsMigrated) {
                            if (presentationOwnerId !== app.user.id && !presentationIsTemplate) {
                                await ShowDialog(VersionConflictDialog, {
                                    title: "This presentation uses an older version of Beautiful.ai",
                                    message: "The owner of this presentation has not yet updated to the latest version of Beautiful.ai. You can continue to edit freely, but without access to the latest UI updates.",
                                    secondButtonLabel: "Got it",
                                    onSecondButtonClick: () => { }
                                });

                                this.resetAppLoaderOnReset = false;
                                window.appLoaderController.forceLegacyApp();
                                return;
                            }

                            presentation.update({ _migrated: true });
                            await presentation.updatePromise;
                        }

                        this.collaborationSlidesLockService = new CollaborationSlidesLockService(presentation.id);
                        await this.collaborationSlidesLockService.initialize();
                        this.collaborationSlidesLockServiceSubscription = this.collaborationSlidesLockService.observable.subscribe(({ prev, curr }) => {
                            const currentSlideId = this.currentSlide?.id;
                            if (!currentSlideId) {
                                return;
                            }
                            const prevLock = prev[currentSlideId];
                            const currLock = curr[currentSlideId];

                            if (!prevLock?.isLockedForMe && currLock?.isLockedForMe) {
                                this._updateState({ currentSlideLockOwner: currLock.lockedBy });
                            } else if (prevLock?.isLockedForMe && !currLock?.isLockedForMe) {
                                this._updateState({ currentSlideLockOwner: null });
                            }

                            if (!prevLock?.isLockedByMe && currLock?.isLockedByMe) {
                                this._updateState({ isCurrentSlideLockedByMe: true });
                            } else if (prevLock?.isLockedByMe && !currLock?.isLockedByMe) {
                                this._updateState({ isCurrentSlideLockedByMe: false });
                            }
                        });
                    }

                    trackState({ presentationId: presentation.id });

                    let recentPresentations = _.clone(app.user.getLibrarySettings().recentPresentations);
                    if (recentPresentations.contains(presentation.id)) {
                        recentPresentations.remove(presentation.id);
                    }
                    recentPresentations.push(presentation.id);
                    if (recentPresentations.length > LIBRARY_RECENT_MAX) {
                        recentPresentations.shift();
                    }
                    app.user.update({
                        librarySettings: { recentPresentations }
                    });

                    // Immediately start loading slides metadata (if presentation is not dummy)
                    this.slidesMetadataLoadPromise =
                        presentationIsDummy
                            ? Promise.resolve({})
                            : presentationsApi.getSlidesMetadata({ id: presentation.id });

                    ds.selection.presentation = presentation;

                    presentation.loadPresentationLinks();

                    // legacy - needed to load the PermissionDataService which still uses reactn
                    permissionsDS.initializeStore();

                    await presentation.prepareSlides(false);

                    const theme = await app.themeManager.loadTheme(presentation);

                    // create canvas and selection layer controllers for all the slides
                    const canvasControllers = {};
                    const selectionLayerControllers = {};
                    for (const slide of presentation.slides.models) {
                        canvasControllers[slide.id] = await this._instantiateCanvasController(presentation, slide);
                        selectionLayerControllers[slide.id] = await this._instantiateSelectionLayerController(canvasControllers[slide.id]);
                        canvasControllers[slide.id].selectionLayerController = selectionLayerControllers[slide.id];

                        if (presentation.slides.models.indexOf(slide) === slideIndex) {
                            // Rendering current slide (so the editor is ready to be used)
                            const canvasController = canvasControllers[slide.id];
                            try {
                                await canvasController.renderCanvas();
                            } catch (err) {
                                logger.error(err, "[PresentationEditorController] error rendering current slide, will retry..", { slideId: slide.id });
                                await new Promise(resolve => setTimeout(resolve, 100)); // Just in case
                                canvasController.renderCanvasPromise = null;
                                await canvasController.renderCanvas()
                                    .catch(err => {
                                        logger.error(err, "[PresentationEditorController] error rendering current slide, will keep in broken state", { slideId: slide.id });
                                    });
                            }
                        }
                    }

                    await this._updateState({
                        presentation,
                        // Keeping references to the slides but busting the reference to the array,
                        // see below for more details
                        slides: [...presentation.slides.models],
                        currentTheme: theme,
                        canvasControllers,
                        selectionLayerControllers
                    });
                    app.currentTheme = theme;

                    await this.setCurrentSlideByIndex(slideIndex, false);

                    presentation.on("change", this._onPresentationChange);
                    presentation.on("slidesUpdated", this._onPresentationSlidesUpdated);

                    await this._updateState({ isInitialized: true });
                })
                .then(resolve)
                .catch(reject);
        });
    }

    _onPresentationChange = presentation => {
        (async () => {
            await this.setPresentation(presentation);

            if (this._state.isInitialized && Object.keys(presentation.changedAttributes() ?? {}).some(key => key.startsWith("theme_") || key === "sharedThemeId")) {
                this.updateTheme();
            }
        })();
    }

    _onPresentationSlidesUpdated = (updatedSlides, options) => {
        // Keeping references to the slides but busting the reference to the array
        // itself so we're mutation safe in cases when slides are added/removed to/from
        // the initial array (e.g. presentation slides)
        this.syncSlidesWithPresentation([...updatedSlides], options);
    }

    _onPresentationRemoteError = error => {
        logger.error(error, "[PresentationEditorController] onPresentationRemoteError()");

        ShowErrorDialog({
            title: "We were unable to update your presentation.",
            message: <>
                {error.status === 409 && <span>
                    Oops! Another user made changes to this presentation at the same time you did. We’ve loaded the most recent version for you to edit.<br />
                    If the problem persists, please contact <a href="mailto:support@beautiful.ai">support@beautiful.ai</a>
                </span>}
                {error.status !== 409 && <span>
                    There may be a temporary problem with your internet connection.<br />
                    If the problem persists, please contact <a href="mailto:support@beautiful.ai">support@beautiful.ai</a>
                </span>}
            </>,
        });
    }

    async syncSlidesWithPresentation(updatedSlides, { isSlideCreatedFromUserAction } = {}) {
        await new Promise((resolve, reject) => {
            this.asyncActionsPromiseChain = this.asyncActionsPromiseChain
                .then(async () => {
                    const { presentation, slides, canvasControllers, selectionLayerControllers, currentSlide } = this._state;

                    const newSlides = [];

                    // Find deleted slides
                    if (_.intersection(slides, updatedSlides).length === 0) {
                        await this.setCurrentSlide(updatedSlides[0], false);
                    } else if (!updatedSlides.contains(currentSlide)) {
                        let currentSlide = this._state.currentSlide;
                        let reversed = false;
                        // First go forward to find the next slide that exists, if not found, then go backwards
                        while (!updatedSlides.contains(currentSlide)) {
                            const slideIndex = slides.findIndex(slide => slide.id === currentSlide.id) + (reversed ? -1 : 1);
                            if (slideIndex === slides.length) {
                                reversed = true;
                                continue;
                            }
                            currentSlide = slides[slideIndex];
                        }

                        await this.setCurrentSlide(currentSlide, false);
                    }

                    const newSelectionLayerControllers = {};
                    const newCanvasControllers = {};
                    for (const slide of updatedSlides) {
                        if (canvasControllers[slide.id]) {
                            // Existing slide
                            newCanvasControllers[slide.id] = canvasControllers[slide.id];
                            const canvas = newCanvasControllers[slide.id].canvas;
                            const footer = canvas?.layouter?.elements.footer;
                            if (footer && !canvas.layouter.isGenerating) {
                                // Force-refresh footer so the page numbers are updated
                                canvas.refreshElement(footer, false, true, false);
                            }
                            // Existing selection layer
                            newSelectionLayerControllers[slide.id] = selectionLayerControllers[slide.id];
                        } else {
                            // New slide
                            const canvasController = await this._instantiateCanvasController(presentation, slide);
                            newCanvasControllers[slide.id] = canvasController;

                            // New selection layer
                            newSelectionLayerControllers[slide.id] = await this._instantiateSelectionLayerController(canvasController);
                            canvasController.selectionLayerController = newSelectionLayerControllers[slide.id];

                            newSlides.push(slide);
                        }

                        newCanvasControllers[slide.id]._state.selectionLayerController = newSelectionLayerControllers[slide.id];
                    }

                    await this._updateState({
                        slides: updatedSlides,
                        canvasControllers: newCanvasControllers,
                        selectionLayerControllers: newSelectionLayerControllers
                    });

                    if (newSlides.length > 0 && isSlideCreatedFromUserAction) {
                        // If the slide was created from a user action, then we need to
                        // update the current slide to the new slide
                        await this.setCurrentSlide(newSlides[0], false);
                    }

                    // Ensure the surrounding slides are rendered in case
                    // new slides were added near the current slide
                    // NOTE: we're not going to await it
                    this.renderCurrentAndSurroundingSlides();
                })
                .then(resolve)
                .catch(reject);
        });
    }

    getCurrentSlide() {
        return this._state.currentSlide;
    }

    goNextSlide() {
        return this.setCurrentSlideByIndex(this.getCurrentSlideIndex() + 1);
    }

    goPrevSlide() {
        return this.setCurrentSlideByIndex(this.getCurrentSlideIndex() - 1);
    }

    _onCurrentSlideChange = slide => {
        const { currentCanvasController, isSingleSlideEditor, showSelectionLayer } = this._state;

        const stateUpdate = { currentSlide: slide };
        if (slide.changedAttributes()?.libraryItemId && showSelectionLayer) {
            stateUpdate.showSelectionLayer = this.canShowSelectionLayer(slide, currentCanvasController, isSingleSlideEditor);
        }

        this._updateState(stateUpdate);
    }

    async setCurrentSlide(slide, runSequentially = true, isTriggeredByPopState) {
        clearTimeout(this.unfreezePropertiesPanelTimeout);

        const setCurrentSlide = async () => {
            const { slides, presentation, canvasControllers, selectionLayerControllers, currentSelectionLayerController, currentCanvasController, currentSlide, isSingleSlideEditor } = this._state;

            if (typeof slide === "string") {
                slide = slides.find(s => s.id === slide);
                if (!slide) {
                    throw new Error(`Slide with id ${slide} not found`);
                }
            }

            if (currentSlide) {
                currentSlide.off("change", this._onCurrentSlideChange);
            }

            if (currentCanvasController) {
                await currentCanvasController.removeAsCurrentCanvas();
            }

            if (currentSelectionLayerController) {
                await currentSelectionLayerController.reset();
            }

            const canvasController = canvasControllers[slide.id];

            // in Video slide in order to show the preview we need to set it as current canvas
            // We do not want to set isSingleSlideEditor to true for video slides
            // because we want to be sure the user won't be able to modify the slide,
            // just see the preview
            const canEditSharedSlide = slide.get("template_id") === "video" || isSingleSlideEditor;

            await canvasController.setAsCurrentCanvas(canEditSharedSlide)
                .catch(err => {
                    logger.error(err, "[PresentationEditorController] error setting canvas as current, will keep in broken state", { slideId: slide.id });
                });

            let currentSlideLockOwner = null;
            if (this.collaborationSlidesLockService) {
                const lockState = this.collaborationSlidesLockService.getLockState(slide.id);
                if (lockState?.isLockedForMe) {
                    currentSlideLockOwner = lockState.lockedBy;
                }
            }

            await this._updateState({
                currentSlide: slide,
                currentCanvasController: canvasController,
                currentSlideLockOwner,
                currentSelectionLayerController: selectionLayerControllers[slide.id],
                freezePropertiesPanel: true,
                showSelectionLayer: this.canShowSelectionLayer(slide, canvasController, isSingleSlideEditor)
            });

            const slideIndex = slide.getIndex();

            // Push the slide index to the url
            clearTimeout(this.pushStateTimeout);
            if (!isSingleSlideEditor && window.history.pushState && !isTriggeredByPopState) {
                // Delaying the pushState call until user settles on a specific slide for smoother transition as current url
                // change triggers a shit ton of analytics scripts that make a lot of network calls on production
                this.pushStateTimeout = setTimeout(() => {
                    browserHistory.push(`/${presentation.id}/${slideIndex + 1}`);
                }, 500);
            }

            // Render surrounding slides
            const PRE_RENDER_RANGE = 2;
            for (let i = Math.max(0, slideIndex - PRE_RENDER_RANGE); i < Math.min(slides.length, slideIndex + PRE_RENDER_RANGE + 1); i++) {
                if (i !== slideIndex) {
                    const slideId = slides[i].id;
                    canvasControllers[slideId].renderCanvas()
                        .catch(err => logger.error(err, "[PresentationEditorController] error rendering slide", { slideId }));
                }
            }

            // Legacy
            ds.selection.slide = slide;

            // Listen for changes to the slide
            slide.on("change", this._onCurrentSlideChange);

            this.unfreezePropertiesPanelTimeout = setTimeout(() => {
                this._updateState({ freezePropertiesPanel: false });
            }, 350);
        };

        if (!runSequentially) {
            return setCurrentSlide();
        }

        return new Promise((resolve, reject) => {
            this.asyncActionsPromiseChain = this.asyncActionsPromiseChain
                .then(setCurrentSlide)
                .then(resolve)
                .catch(reject);
        });
    }

    async setCurrentSlideByIndex(index, runSequentially = true, isTriggeredByPopState = false) {
        const { slides } = this._state;
        index = Math.clamp(index, 0, slides.length - 1);
        await this.setCurrentSlide(slides[index], runSequentially, isTriggeredByPopState);
    }

    async setCurrentSlideById(id) {
        const { slides } = this._state;
        const targetSlide = slides.find(slide => slide.id === id);
        if (!targetSlide) {
            throw new Error(`Slide with id ${id} not found`);
        }
        await this.setCurrentSlide(targetSlide);
    }

    async showAddSlideDialog() {
        const { AddSlideContainer } = await import(/* webpackMode: "eager" */ "js/react/views/AddSlide");
        await this.currentCanvasController.removeAsCurrentCanvas();
        await ShowDialogAsync(AddSlideContainer);
        await this.currentCanvasController.setAsCurrentCanvas();
    }

    async duplicateSlide() {
        const { presentation, currentSlide } = this._state;

        const insertIndex = this.getCurrentSlideIndex() + 1;

        const [newSlide] = await presentation.duplicateSlide(currentSlide, { insertIndex, isSlideCreatedFromUserAction: true });

        const { slideTemplates: { slideTemplates } } = await getCanvasBundle(newSlide.get("version") ?? appVersion);
        const duplicateSlideCount = 1;
        const eventProps = {
            "slide_ids": [newSlide.get("id")],
            "source_slides": [currentSlide.get("id")],
            "slide_template_names": [slideTemplates[currentSlide.get("template_id")].title],
            "slides_created": duplicateSlideCount,
        };
        trackActivity("Slide", "Duplicate", null, duplicateSlideCount, eventProps, { audit: true });
    }

    async deleteSlide() {
        const { presentation, currentSlide, currentCanvasController } = this._state;

        if (presentation.slides.length <= 1) {
            ShowWarningDialog({
                title: "Can't delete slide",
                message: "Sorry, you can't delete the only slide in a presentation"
            });
            return;
        }

        await this.hideCanvasControls();
        await this.hideSelectionLayer();

        const slideIndex = this.getCurrentSlideIndex();
        if (slideIndex === presentation.slides.length - 1) {
            await this.setCurrentSlideByIndex(slideIndex - 1);
        } else {
            await this.setCurrentSlideByIndex(slideIndex + 1);
        }

        await currentCanvasController.setOpacity(0);

        await new Promise(resolve => setTimeout(resolve, 300));

        await presentation.destroySlides(currentSlide.id);

        await this.showCanvasControls();
    }

    async renamePresentation() {
        const { presentation } = this._state;

        const presentationName = await ShowInputDialog({
            title: "Enter a name for your presentation",
            value: presentation.get("name"),
            trimInput: true
        });

        if (!presentationName) {
            return;
        }

        await presentation.rename(presentationName);
    }

    undo() {
        app.undoManager.undo();
    }

    redo() {
        app.undoManager.redo();
    }

    isSlideGridVisible() {
        return this._state.showSlideGrid;
    }

    toggleSlideGrid() {
        if (this._state.showSlideGrid) {
            return this.hideSlideGrid();
        }

        return this.showSlideGrid();
    }

    async showSlideGrid() {
        await this.currentCanvasController.removeAsCurrentCanvas();

        await this._updateState({
            showSlideGrid: true,
            showSelectionLayer: false,
            showCanvasControls: false
        });
    }

    async hideSlideGrid() {
        const { currentSlide, currentCanvasController, isSingleSlideEditor } = this._state;

        await this.currentCanvasController.setAsCurrentCanvas();

        await this._updateState({
            showSlideGrid: false,
            showSelectionLayer: this.canShowSelectionLayer(currentSlide, currentCanvasController, isSingleSlideEditor),
            showCanvasControls: true
        });
    }

    setPopupState(popupOpenState) {
        return this._updateState({
            popupOpenState
        });
    }

    showCanvasControls() {
        return this._updateState({ showCanvasControls: true });
    }

    hideCanvasControls() {
        return this._updateState({ showCanvasControls: false });
    }

    async showRightPanel(type) {
        const { currentCanvasController } = this._state;

        await this._updateState({
            activeRightPanel: type,
            activeBottomPanel: null
        });

        currentCanvasController.lockSlideForCollaborators();
    }

    async showBottomPanel(type) {
        const { currentCanvasController } = this._state;

        await this._updateState({
            activeBottomPanel: type,
            activeRightPanel: null
        });

        currentCanvasController.lockSlideForCollaborators();
    }

    async showElementPanel(forElement) {
        const { currentCanvasController } = this._state;

        await this._updateState({
            activeBottomPanel: PanelType.ELEMENT,
            ElementPanel: forElement.getElementPanel(),
            elementPanelElement: forElement
        });

        currentCanvasController.lockSlideForCollaborators();
    }

    async closeRightPanel() {
        const { currentCanvasController, currentSlide, isSingleSlideEditor } = this._state;

        await this._updateState({
            activeRightPanel: null,
            showSelectionLayer: this.canShowSelectionLayer(currentSlide, currentCanvasController, isSingleSlideEditor),
        });
    }

    async closeBottomPanel() {
        const { currentCanvasController, currentSlide, isSingleSlideEditor } = this._state;

        await this._updateState({
            activeBottomPanel: null,
            ElementPanel: null,
            elementPanelElement: null,
            showSelectionLayer: this.canShowSelectionLayer(currentSlide, currentCanvasController, isSingleSlideEditor),
        });
    }

    showEditorPopup(value) {
        return this._updateState({
            showEditorPopup: value,
        });
    }

    toggleComments() {
        const { activeRightPanel } = this._state;

        const isCommentsPanelActive = activeRightPanel === PanelType.COMMENTS;

        if (isCommentsPanelActive) {
            return this.closeRightPanel();
        }

        return this.showRightPanel(PanelType.COMMENTS);
    }

    toggleAssignSlide() {
        const { activeRightPanel } = this._state;

        const isCommentsPanelActive = activeRightPanel === PanelType.ASSIGN_SLIDE;

        if (isCommentsPanelActive) {
            return this.closeRightPanel();
        }

        return this.showRightPanel(PanelType.ASSIGN_SLIDE);
    }

    get isNotesPanelActive() {
        const { activeBottomPanel } = this._state;
        return activeBottomPanel === PanelType.NOTES;
    }

    toggleNotes() {
        if (this.isNotesPanelActive) {
            return this.closeBottomPanel();
        }

        return this.showBottomPanel(PanelType.NOTES);
    }

    async showSelectionLayer() {
        const { currentCanvasController, currentSlide, isSingleSlideEditor } = this._state;

        await this._updateState({
            showSelectionLayer: this.canShowSelectionLayer(currentSlide, currentCanvasController, isSingleSlideEditor)
        });

        currentCanvasController.setEditable(true);
    }

    async hideSelectionLayer() {
        const { currentCanvasController, currentSelectionLayerController } = this._state;

        if (currentSelectionLayerController) {
            await currentSelectionLayerController.setSelectedElements([]);
        }

        await this._updateState({
            showSelectionLayer: false
        });

        currentCanvasController.setEditable(false);
    }

    setSkipSlide(slide, value) {
        const { presentation } = this._state;
        presentation.skipSlide(slide, value);
    }

    async playAnimation() {
        await this.hideSelectionLayer();

        await this.getCurrentCanvasController().canvas.animate();

        await this.showSelectionLayer();
    }

    async exportPresentation() {
        const { default: ExportDialog } = await import(/* webpackMode: "eager" */ "js/react/views/PresentationSettings/dialogs/ExportDialog");

        await ShowDialogAsync(ExportDialog, { presentation: this._state.presentation });
    }

    async updateTheme() {
        const { presentation, currentSelectionLayerController } = this._state;

        if (currentSelectionLayerController) {
            await currentSelectionLayerController.setSelectedElements([]);
        }

        const progressDialog = ShowDialog(ProgressDialog, {
            title: "Applying theme...",
            progress: 0
        });

        const theme = await app.themeManager.loadTheme(presentation);
        await this._updateState({ currentTheme: theme });
        app.currentTheme = theme;

        await this.refreshAllCanvases(progressDialog);

        progressDialog.props.closeDialog();
    }

    async refreshAllCanvases(progressDialog) {
        const canvases = this.getCanvases();
        const slideCount = canvases.length;

        let slidesRefreshed = 1;
        for (const canvas of canvases) {
            await canvas.loadStyles(true);
            canvas.layouter.canvasElement.markStylesAsDirty();
            await canvas.refreshCanvas({ forceRender: false });
            if (progressDialog) {
                progressDialog.setProgress(slidesRefreshed / slideCount * 100);
            }
            slidesRefreshed++;
        }
    }

    async updateSlideVersion(slide, slideVersion = appVersion) {
        const confirmed = await ShowConfirmationDialog({
            title: "Hey, this Smart Slide got a bit smarter!",
            showRobot: true,
            optOutProperty: "updateSlideConfirmationDialog",
            message: (
                <>
                    <p>Occasionally, we make improvements or add new features to our Smart Slides.</p>
                    <p>Updating your slide is completely <strong>optional</strong> but if you'd like to take
                        advantage of the improvements on this existing slide, you can update it using the <strong>Update
                            Slide</strong> button.</p>
                    <blockquote>
                        <p><strong>NOTE</strong> In some cases, features, styles, or appearance may be different
                            from the previous version.</p>
                        <p>If you are unhappy with any changes, you can undo or revert to a previous version of your
                            slide using the version history.</p>
                    </blockquote>
                </>
            ),
            okButtonLabel: "Update Slide"
        });

        if (!confirmed) {
            return;
        }

        slide.commit({ undo: false }); // Preserve changes (if any)

        const canvasController = this.getCanvasControllerForSlide(slide);

        // Reload the canvas forcing to the current slide version
        await canvasController.reloadCanvas(slideVersion);

        // Save the migration immediately
        await canvasController.canvas.saveCanvasModel();

        await this.setCurrentSlide(slide);
    }

    async setSelectedPropertyPanelTab(selectedPropertyPanelTab, selectedPropertyPanelTabProps) {
        await this._updateState({ selectedPropertyPanelTab, selectedPropertyPanelTabProps });
    }

    togglePropertyPanel() {
        this._updateState({ showPropertyPanel: !this._state.showPropertyPanel });
    }

    showPropertyPanel() {
        this._updateState({ showPropertyPanel: true });
    }

    hidePropertyPanel() {
        this._updateState({ showPropertyPanel: false });
    }
}

const presentationEditorController = new PresentationEditorController(_.cloneDeep(initialState));
export default presentationEditorController;

if (!app.mainView) {
    app.mainView = {};
}

app.mainView.editorView = {
    lockModel: {
        setLockState: () => {
        }
    },
    getContainerBounds: () => {
        return {
            canvasBounds: new geom.Rect(0, 0, 1280, 1656)
        };
    }
};

app.presentationEditorController = presentationEditorController;
